/**
 * The contract between the app and whichever AI model reads the receipt.
 *
 * {@link RECEIPT_JSON_SCHEMA} is handed to providers that support structured
 * output (Anthropic tool use, OpenAI `json_schema`, Ollama `format`). Models
 * that only speak prose get the same shape described in the prompt. Either way
 * the response goes through {@link normalizeExtraction}, which is written to
 * assume nothing about how well the model followed instructions.
 */

import {
  normalizeOrgNumber,
  normalizeSearchName,
  normalizeVatNumber,
  parseAmount,
  parseLocalDateTime,
  parseQuantity,
  roundMoney,
} from './parse.js';
import { emptyMerchant, ITEM_UNITS, type ItemUnit, type Merchant, type VatLine } from './types.js';

/** Whatever the model returned, before any coercion. */
export type RawExtraction = Record<string, unknown>;

/** A normalised, ready-to-store extraction result. */
export interface NormalizedExtraction {
  merchant: Merchant;
  purchasedAt: string | null;
  currency: string;
  total: number | null;
  subtotal: number | null;
  discountTotal: number | null;
  roundingAmount: number | null;
  depositTotal: number | null;
  vatLines: VatLine[];
  paymentMethod: string | null;
  cardLast4: string | null;
  receiptNumber: string | null;
  terminalId: string | null;
  cashier: string | null;
  items: NormalizedItem[];
  /**
   * The category key the model gave the whole receipt, as it wrote it. Not yet
   * checked against the list it was offered: see `resolveCategoryKey`.
   */
  categoryKey: string | null;
  /** Further category keys the model gave the receipt, as written, unchecked. */
  otherCategoryKeys: string[];
  /** Model-reported confidence in 0..1, when offered. */
  confidence: number | null;
  warnings: string[];
}

export interface NormalizedItem {
  name: string;
  rawName: string | null;
  searchName: string;
  quantity: number;
  unit: ItemUnit;
  unitPrice: number | null;
  totalPrice: number;
  discount: number | null;
  vatRate: number | null;
  ean: string | null;
  deposit: number | null;
  isDeposit: boolean;
  isDiscount: boolean;
  /** The category key the model gave this line, unchecked, like `NormalizedExtraction.categoryKey`. */
  categoryKey: string | null;
  /** How sure the model was of `categoryKey`, 0..1, when it said. */
  categoryScore: number | null;
  /**
   * Other categories the line could be, as the model wrote their keys, best
   * first, unchecked; never `categoryKey` itself. Usually empty.
   */
  alternativeCategoryKeys: { key: string; score: number | null }[];
}

const VALID_UNITS: ReadonlySet<string> = new Set<ItemUnit>(ITEM_UNITS);

/** Words that mark a line as a bottle/can deposit rather than a product. */
const DEPOSIT_PATTERN = /\bpant\b/i;
/** Words Swedish stores print on discount lines, and only there. */
const DISCOUNT_PATTERN = /(rabatt|prisneds[äa]ttning|stammis|avdrag|\brea\b|prisjustering)/i;
/**
 * Words that name an offer: printed on the discount line, but as often on the
 * product it applies to ("RÖD PEPERONI Kampanj 24,90"). On their own they
 * make a line a discount only when it takes money off.
 */
const OFFER_PATTERN = /(kampanj|erbjudande|extrapris|medlemspris|klubbpris)/i;

/**
 * Whether a line is a pant row or a discount row rather than a product,
 * from its printed name and its price.
 *
 * `returned` is for a return (see `looksLikeReturn`), where every product is
 * printed negative: there a minus sign alone does not make a discount.
 */
export function lineKind(
  name: string,
  rawName: string | null,
  totalPrice: number | null,
  returned = false,
): { isDeposit: boolean; isDiscount: boolean } {
  const haystack = `${name} ${rawName ?? ''}`;
  const isDeposit = DEPOSIT_PATTERN.test(haystack);
  // A negative line that is not pant is a discount, whether or not it says so.
  const negative = !returned && totalPrice !== null && totalPrice < 0;
  const offerOff = OFFER_PATTERN.test(haystack) && (totalPrice === null || totalPrice <= 0);
  const isDiscount = !isDeposit && (DISCOUNT_PATTERN.test(haystack) || offerOff || negative);
  return { isDeposit, isDiscount };
}

/**
 * Whether a raw reading is of a return: its total is negative, or - with no
 * total read - its lines add up to less than nothing.
 */
function isReturnReading(total: number | null, items: unknown[]): boolean {
  if (total !== null) return total < 0;
  const sum = items.reduce<number>((acc, entry) => {
    if (!entry || typeof entry !== 'object') return acc;
    return acc + (parseAmount(pickUnknown(entry as Record<string, unknown>, 'totalPrice', 'total_price', 'total', 'amount', 'price')) ?? 0);
  }, 0);
  return sum < 0;
}

/** `{ "type": ["string", "null"] }` — the shape every optional text field takes. */
const nullableString = { type: ['string', 'null'] } as const;

/**
 * JSON Schema describing the extraction result.
 *
 * Handed to providers that support structured output (Anthropic
 * `output_config.format`, OpenAI `json_schema`, Ollama `format`). Two
 * deliberate choices:
 *
 * 1. **Every amount is a string.** The model transcribes what is printed
 *    (`"1 234,50"`, `"25,00-"`) and {@link normalizeExtraction} converts it with
 *    the Swedish number parser. Asking a model to also do locale conversion is
 *    where silent off-by-100 errors come from, and a string round-trips the
 *    printed form exactly.
 * 2. **Strict-mode friendly.** Every property is listed in `required` and every
 *    object sets `additionalProperties: false`, because strict structured-output
 *    modes reject schemas that do not. Optionality is expressed as a `null`
 *    union instead.
 */
export const RECEIPT_JSON_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: [
    'merchant', 'purchasedAt', 'currency', 'total', 'subtotal', 'discountTotal',
    'roundingAmount', 'depositTotal', 'vatLines', 'paymentMethod', 'cardLast4',
    'receiptNumber', 'terminalId', 'cashier', 'items', 'confidence',
  ],
  properties: {
    merchant: {
      type: 'object',
      additionalProperties: false,
      required: ['name', 'orgNumber', 'vatNumber', 'address', 'postalCode', 'city', 'country', 'phone', 'storeId'],
      properties: {
        name: { ...nullableString, description: 'Store name, e.g. "ICA Kvantum Emporia".' },
        orgNumber: { ...nullableString, description: 'Swedish organisationsnummer as printed.' },
        vatNumber: { ...nullableString, description: 'Momsregistreringsnummer.' },
        address: nullableString,
        postalCode: nullableString,
        city: nullableString,
        country: nullableString,
        phone: nullableString,
        storeId: {
          ...nullableString,
          // Not "kassanummer": saying both here and under `terminalId` had
          // models file one number under both, and the till under the store.
          description: 'The store\'s own number, printed after "Butik", "Butiksnr" or "Butiksnummer". Never the till.',
        },
      },
    },
    purchasedAt: {
      ...nullableString,
      description: 'Date and time exactly as printed, e.g. "2024-03-15 14:22" or "15/3-24".',
    },
    currency: { ...nullableString, description: 'ISO 4217 code. "SEK" unless the receipt says otherwise.' },
    total: { ...nullableString, description: 'Att betala — the amount actually paid, as printed.' },
    subtotal: { ...nullableString, description: 'Sum before receipt-level discounts, as printed.' },
    discountTotal: { ...nullableString, description: 'Receipt-level discount total, as printed.' },
    roundingAmount: { ...nullableString, description: 'Öresavrundning, as printed. May be negative.' },
    depositTotal: { ...nullableString, description: 'Total pant on the receipt, as printed.' },
    vatLines: {
      type: 'array',
      description: 'One entry per row of the Moms/VAT summary table. Empty array if there is none.',
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['rate', 'net', 'vat', 'gross'],
        properties: {
          rate: { type: 'string', description: 'Percentage as printed, e.g. "12" or "12,00".' },
          net: { ...nullableString, description: 'Netto column.' },
          vat: { ...nullableString, description: 'Moms column.' },
          gross: { ...nullableString, description: 'Brutto column.' },
        },
      },
    },
    paymentMethod: {
      ...nullableString,
      description: 'e.g. "Kontokort", "Swish", "Kontant", "Presentkort".',
    },
    cardLast4: { ...nullableString, description: 'Last four digits of the card, if shown.' },
    receiptNumber: nullableString,
    terminalId: {
      ...nullableString,
      description: 'The till or card terminal, printed after "Kassa", "Kassanr", "Terminal" or "TID". Never the store number.',
    },
    cashier: {
      ...nullableString,
      description:
        'The person who served, printed after "Kassör", "Expedit" or "Betjänad av". Null for a self-checkout ("Självcheckout", "Självscanning").',
    },
    items: {
      type: 'array',
      description: 'Every printed line, in order, including pant and discount rows.',
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['name', 'rawName', 'quantity', 'unit', 'unitPrice', 'totalPrice', 'discount', 'vatRate', 'ean', 'deposit'],
        properties: {
          name: {
            type: 'string',
            description: 'Product name with obvious abbreviations expanded, e.g. "MJÖLK MELLAN 1,5%" → "Mjölk mellan 1,5 %".',
          },
          rawName: { ...nullableString, description: 'The line exactly as printed.' },
          quantity: { ...nullableString, description: 'As printed, e.g. "2" or "0,412".' },
          unit: { ...nullableString, description: 'One of st, kg, hg, g, l, dl, cl, m, förp.' },
          unitPrice: { ...nullableString, description: 'Price per unit before discount, as printed.' },
          totalPrice: {
            type: 'string',
            description: 'What this line contributed to the total, as printed. Negative for discounts.',
          },
          discount: { ...nullableString, description: 'Discount applied to this line, as printed.' },
          vatRate: { ...nullableString, description: 'VAT percentage for this line, if shown.' },
          ean: { ...nullableString, description: 'EAN/GTIN barcode digits, if printed.' },
          deposit: { ...nullableString, description: 'Pant on this line, if broken out.' },
        },
      },
    },
    confidence: {
      type: ['number', 'null'],
      description: 'How confident you are that the whole receipt was read correctly, 0..1.',
    },
  },
} as const;

const categoryAnswer = {
  ...nullableString,
  description: 'A key from the category list, exactly as written there, or null.',
} as const;

const categoryConfidenceAnswer = {
  type: ['number', 'null'],
  description: 'How sure you are of this category, 0..1.',
} as const;

const alternativeCategoriesAnswer = {
  type: 'array',
  description:
    'Up to three other categories this line could plausibly be instead, most likely first, never the category ' +
    'already given. Usually empty.',
  items: {
    type: 'object',
    additionalProperties: false,
    required: ['category', 'confidence'],
    properties: {
      category: { type: 'string', description: 'A key from the category list, exactly as written there.' },
      confidence: { type: 'number', description: 'How likely the line is this category instead, 0..1.' },
    },
  },
} as const;

/** What a line is filed with: its category, how sure the model was, and what else it could be. */
const lineFilingAnswers = {
  category: categoryAnswer,
  categoryConfidence: categoryConfidenceAnswer,
  alternativeCategories: alternativeCategoriesAnswer,
} as const;
const LINE_FILING_FIELDS = Object.keys(lineFilingAnswers) as (keyof typeof lineFilingAnswers)[];

const otherCategoriesAnswer = {
  type: 'array',
  description: 'Further main categories the purchase clearly also covers, as keys from the list. Usually empty.',
  items: { type: 'string' },
} as const;

/**
 * {@link RECEIPT_JSON_SCHEMA}, with a `category` and `otherCategories` on the
 * receipt and a `category` - with its `categoryConfidence` and
 * `alternativeCategories` - on every item when the request offers a category list.
 *
 * A plain string rather than an `enum` of the keys: the list is the user's and
 * changes, and the answer is checked against it afterwards either way.
 */
export function receiptJsonSchema(withCategories: boolean): Record<string, unknown> {
  if (!withCategories) return RECEIPT_JSON_SCHEMA;
  const { items } = RECEIPT_JSON_SCHEMA.properties;
  return {
    ...RECEIPT_JSON_SCHEMA,
    required: [...RECEIPT_JSON_SCHEMA.required, 'category', 'otherCategories'],
    properties: {
      ...RECEIPT_JSON_SCHEMA.properties,
      category: { ...categoryAnswer, description: 'The main category of the purchase as a whole. ' + categoryAnswer.description },
      otherCategories: otherCategoriesAnswer,
      items: {
        ...items,
        items: {
          ...items.items,
          required: [...items.items.required, ...LINE_FILING_FIELDS],
          properties: { ...items.items.properties, ...lineFilingAnswers },
        },
      },
    },
  };
}

/**
 * The answer of the categorising follow-up call: a category per numbered line
 * and one for the receipt. See `buildCategorySystemPrompt`.
 */
export const CATEGORY_JSON_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['receiptCategory', 'otherCategories', 'items'],
  properties: {
    receiptCategory: { ...categoryAnswer, description: 'The main category of the purchase as a whole. ' + categoryAnswer.description },
    otherCategories: otherCategoriesAnswer,
    items: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['line', ...LINE_FILING_FIELDS],
        properties: {
          line: { type: 'integer', description: 'The line number as given.' },
          ...lineFilingAnswers,
        },
      },
    },
  },
} as const;

/**
 * The compact answer of the filing call (`buildCompactCategorySystemPrompt`):
 * `{"receipt": …, "lines": {"1": …, "2": …}}`, for one receipt's lines. Every
 * line number is required and every value is one of the offered names, so a
 * runtime that enforces the schema while decoding cannot leave a line out or
 * name a category that is not on the list. The names are defined once and
 * referenced, so a long receipt does not repeat the list for every line.
 */
export function compactCategorySchema(categories: readonly { key: string }[], lineNumbers: readonly number[]): Record<string, unknown> {
  const category = { $ref: '#/$defs/category' };
  return {
    type: 'object',
    additionalProperties: false,
    required: ['receipt', 'lines'],
    $defs: { category: { type: 'string', enum: [...new Set(categories.map((option) => option.key))] } },
    properties: {
      receipt: category,
      lines: {
        type: 'object',
        additionalProperties: false,
        required: lineNumbers.map(String),
        properties: Object.fromEntries(lineNumbers.map((line) => [String(line), category])),
      },
    },
  };
}

/**
 * How a model filed one line, from an item of a reading or of a filing
 * answer: the category key as it wrote it, or - when it also said how sure it
 * was or what else the line could be - those fields with it, to be written
 * onto the item as they are (see `readingFrom`). Plain when there is nothing
 * more, so a filing without them reads as it always has.
 */
export function lineFiling(row: Record<string, unknown>): unknown {
  const category = row.category ?? null;
  const confidence = row.categoryConfidence ?? null;
  const alternatives = Array.isArray(row.alternativeCategories) ? row.alternativeCategories : [];
  if (confidence === null && alternatives.length === 0) return category;
  return { category, categoryConfidence: confidence, alternativeCategories: alternatives };
}

/** A model's 0..1 score, clamped, or `null` for anything that is not a number. */
function normalizeScore(input: unknown): number | null {
  const value = typeof input === 'string' ? Number(input) : input;
  return typeof value === 'number' && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : null;
}

/**
 * The other categories a model gave a line, each key once, never its own,
 * best first: by score where it gave one, else in its order. Bare keys are
 * taken too, for a model that ignored the shape.
 */
function normalizeAlternatives(source: unknown[], own: string | null): { key: string; score: number | null }[] {
  const out: { key: string; score: number | null }[] = [];
  const seen = new Set(own ? [own.toLowerCase()] : []);
  for (const entry of source) {
    const row = entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : null;
    const key = row ? pickString(row, 'category', 'key') : typeof entry === 'string' ? entry.trim() : null;
    if (!key || seen.has(key.toLowerCase())) continue;
    seen.add(key.toLowerCase());
    out.push({ key, score: row ? normalizeScore(pickUnknown(row, 'confidence', 'score')) : null });
  }
  return out
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => (b.entry.score ?? -1) - (a.entry.score ?? -1) || a.index - b.index)
    .map(({ entry }) => entry);
}

/**
 * Coerces an arbitrary model response into {@link NormalizedExtraction}.
 * Never throws: anything unusable becomes a warning and a `null`.
 */
export function normalizeExtraction(raw: RawExtraction): NormalizedExtraction {
  const warnings: string[] = [];
  const merchant = normalizeMerchant(pickObject(raw, 'merchant', 'store', 'seller'));

  const currencyRaw = pickString(raw, 'currency');
  const currency = currencyRaw && /^[A-Za-z]{3}$/.test(currencyRaw)
    ? currencyRaw.toUpperCase()
    : 'SEK';

  const purchasedAtRaw =
    pickString(raw, 'purchasedAt', 'purchased_at', 'date', 'datetime', 'dateTime');
  const purchasedAt = parseLocalDateTime(purchasedAtRaw);
  if (purchasedAtRaw && !purchasedAt) {
    warnings.push(`Could not read the purchase date from "${purchasedAtRaw}".`);
  }

  let total = parseAmount(pickUnknown(raw, 'total', 'totalAmount', 'amountPaid', 'attBetala'));
  const rawItems = pickArray(raw, 'items', 'lineItems', 'line_items', 'products');
  const items = normalizeItems(rawItems, warnings, isReturnReading(total, rawItems));
  const vatLines = normalizeVatLines(pickArray(raw, 'vatLines', 'vat_lines', 'vat', 'moms'));

  const subtotal = parseAmount(pickUnknown(raw, 'subtotal', 'sum', 'netTotal'));
  const roundingAmount = parseAmount(pickUnknown(raw, 'roundingAmount', 'rounding', 'oresavrundning'));

  const discountRaw = parseAmount(pickUnknown(raw, 'discountTotal', 'discount', 'totalDiscount'));
  const discountTotal = discountRaw === null ? null : Math.abs(discountRaw);

  let depositTotal = parseAmount(pickUnknown(raw, 'depositTotal', 'deposit', 'pant'));
  if (depositTotal === null) {
    const fromLines = items
      .filter((item) => item.isDeposit)
      .reduce((sum, item) => sum + item.totalPrice, 0);
    depositTotal = fromLines === 0 ? null : roundMoney(fromLines);
  }

  // A missing total is recoverable: the VAT table's gross column, or the line
  // items, both add up to the same number on a well-formed receipt.
  if (total === null) {
    const grossSum = vatLines.reduce((sum, line) => sum + (line.gross ?? 0), 0);
    if (grossSum > 0) {
      total = roundMoney(grossSum);
      warnings.push('Total was missing; derived it from the VAT summary.');
    } else if (items.length > 0) {
      total = roundMoney(items.reduce((sum, item) => sum + item.totalPrice, 0));
      warnings.push('Total was missing; derived it by summing the line items.');
    } else {
      warnings.push('No total could be found on this receipt.');
    }
  }

  const confidenceRaw = pickUnknown(raw, 'confidence');
  const confidence = typeof confidenceRaw === 'number' ? normalizeScore(confidenceRaw) : null;

  return {
    merchant,
    purchasedAt,
    currency,
    total,
    subtotal,
    discountTotal,
    roundingAmount,
    depositTotal,
    vatLines,
    paymentMethod: pickString(raw, 'paymentMethod', 'payment_method', 'payment'),
    cardLast4: normalizeLast4(pickString(raw, 'cardLast4', 'card_last4', 'cardNumber')),
    receiptNumber: pickString(raw, 'receiptNumber', 'receipt_number', 'kvittonummer'),
    terminalId: pickString(raw, 'terminalId', 'terminal', 'kassa'),
    cashier: pickString(raw, 'cashier', 'kassör', 'kassor'),
    items,
    categoryKey: pickString(raw, 'category'),
    otherCategoryKeys: pickArray(raw, 'otherCategories')
      .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
      .filter(Boolean),
    confidence,
    warnings,
  };
}

function normalizeMerchant(source: Record<string, unknown> | null): Merchant {
  const merchant = emptyMerchant();
  if (!source) return merchant;
  merchant.name = pickString(source, 'name', 'merchant', 'store', 'storeName');
  merchant.orgNumber = normalizeOrgNumber(pickString(source, 'orgNumber', 'org_number', 'orgnr'));
  merchant.vatNumber = normalizeVatNumber(pickString(source, 'vatNumber', 'vat_number', 'momsnr'));
  merchant.address = pickString(source, 'address', 'street');
  merchant.postalCode = pickString(source, 'postalCode', 'postal_code', 'zip');
  merchant.city = pickString(source, 'city', 'town', 'ort');
  merchant.country = pickString(source, 'country');
  merchant.phone = pickString(source, 'phone', 'tel', 'telefon');
  merchant.storeId = pickString(source, 'storeId', 'store_id', 'butiksnummer');
  return merchant;
}

function normalizeItems(source: unknown[], warnings: string[], returned: boolean): NormalizedItem[] {
  const items: NormalizedItem[] = [];
  for (const entry of source) {
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;

    const rawName = pickString(row, 'rawName', 'raw_name', 'printedName');
    const name = (pickString(row, 'name', 'description', 'product', 'title') ?? rawName ?? '').trim();
    if (!name) continue;

    const totalPrice = parseAmount(pickUnknown(row, 'totalPrice', 'total_price', 'total', 'amount', 'price'));
    if (totalPrice === null) {
      warnings.push(`Skipped "${name}" because it had no readable price.`);
      continue;
    }

    const { quantity, unit: parsedUnit } = parseQuantity(pickUnknown(row, 'quantity', 'qty', 'antal'));
    const explicitUnit = (pickString(row, 'unit', 'enhet') ?? '').toLowerCase().replace(/\./g, '');
    const unit = (VALID_UNITS.has(explicitUnit) ? explicitUnit : parsedUnit) as ItemUnit;

    const discountRaw = parseAmount(pickUnknown(row, 'discount', 'rabatt'));
    const depositRaw = parseAmount(pickUnknown(row, 'deposit', 'pant'));

    const { isDeposit, isDiscount } = lineKind(name, rawName, totalPrice, returned);
    const categoryKey = pickString(row, 'category');

    let unitPrice = parseAmount(pickUnknown(row, 'unitPrice', 'unit_price', 'jmfpris', 'styckpris'));
    if (unitPrice === null && quantity > 0 && !isDiscount) {
      unitPrice = roundMoney(totalPrice / quantity);
    }

    items.push({
      name,
      rawName,
      searchName: normalizeSearchName(name),
      quantity,
      unit,
      unitPrice,
      totalPrice,
      discount: discountRaw === null ? null : Math.abs(discountRaw),
      vatRate: normalizeVatRate(pickUnknown(row, 'vatRate', 'vat_rate', 'moms', 'momssats')),
      ean: normalizeEan(pickString(row, 'ean', 'gtin', 'barcode')),
      deposit: depositRaw === null ? null : Math.abs(depositRaw),
      isDeposit,
      isDiscount,
      categoryKey,
      categoryScore: categoryKey ? normalizeScore(pickUnknown(row, 'categoryConfidence')) : null,
      alternativeCategoryKeys: normalizeAlternatives(pickArray(row, 'alternativeCategories'), categoryKey),
    });
  }
  return items;
}

function normalizeVatLines(source: unknown[]): VatLine[] {
  const lines: VatLine[] = [];
  for (const entry of source) {
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;
    const rate = normalizeVatRate(pickUnknown(row, 'rate', 'percent', 'momssats', 'sats'));
    if (rate === null) continue;

    const net = parseAmount(pickUnknown(row, 'net', 'netto', 'excl'));
    const vat = parseAmount(pickUnknown(row, 'vat', 'moms', 'tax'));
    let gross = parseAmount(pickUnknown(row, 'gross', 'brutto', 'incl', 'total'));
    if (gross === null && net !== null && vat !== null) gross = roundMoney(net + vat);
    lines.push({ rate, net, vat, gross });
  }
  return lines;
}

/** Swedish VAT is 0, 6, 12 or 25 percent. Accepts `0.12` and `"12 %"` alike. */
function normalizeVatRate(input: unknown): number | null {
  const parsed = parseAmount(input);
  if (parsed === null) return null;
  const value = Math.abs(parsed);
  if (value === 0) return 0;
  // A fraction like 0.12 means 12 %.
  const percent = value < 1 ? value * 100 : value;
  if (percent > 100) return null;
  return roundMoney(percent);
}

function normalizeEan(input: string | null): string | null {
  if (!input) return null;
  const digits = input.replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 14 ? digits : null;
}

function normalizeLast4(input: string | null): string | null {
  if (!input) return null;
  const digits = input.replace(/\D/g, '');
  return digits.length >= 4 ? digits.slice(-4) : null;
}

// --- tolerant field access ------------------------------------------------

function pickUnknown(source: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
}

function pickString(source: Record<string, unknown>, ...keys: string[]): string | null {
  const value = pickUnknown(source, ...keys);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    // Models like to fill blanks with these instead of null.
    if (!trimmed || /^(n\/?a|unknown|okänd|null|-|--)$/i.test(trimmed)) return null;
    return trimmed;
  }
  if (typeof value === 'number') return String(value);
  return null;
}

function pickObject(source: Record<string, unknown>, ...keys: string[]): Record<string, unknown> | null {
  const value = pickUnknown(source, ...keys);
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function pickArray(source: Record<string, unknown>, ...keys: string[]): unknown[] {
  const value = pickUnknown(source, ...keys);
  return Array.isArray(value) ? value : [];
}
